import React, { useState, useEffect, useMemo } from 'react';
import { Play, Timer, Trophy, AlertCircle, RefreshCw } from 'lucide-react';

const GRID_SIZE = 100;
const PRIMES_TO_SIEVE = [2, 3, 5, 7];

const SieveSpeedrun = () => {
  const [gameState, setGameState] = useState('menu'); // menu, playing, won
  const [numbers, setNumbers] = useState([]);
  const [currentPrimeIndex, setCurrentPrimeIndex] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [message, setMessage] = useState("");

  const currentPrime = PRIMES_TO_SIEVE[currentPrimeIndex];

  // Initialize Numbers
  useEffect(() => {
    if (gameState === 'menu') {
        const nums = Array.from({ length: GRID_SIZE }, (_, i) => ({
            value: i + 1,
            status: 'active', // active, eliminated, prime
            isPrime: false // Will be revealed at end
        }));
        setNumbers(nums);
    }
  }, [gameState]);

  // Timer
  useEffect(() => {
    let interval;
    if (gameState === 'playing') {
      interval = setInterval(() => {
        setElapsedTime(Date.now() - startTime);
      }, 100);
    }
    return () => clearInterval(interval);
  }, [gameState, startTime]);

  const startGame = () => {
    const nums = Array.from({ length: GRID_SIZE }, (_, i) => ({
        value: i + 1,
        status: 'active',
        isPrime: false
    }));
    // 1 is neither prime nor composite, usually crossed out or ignored. Let's mark it eliminated initially or let user ignore it.
    // Standard sieve: Cross out 1.
    nums[0].status = 'eliminated';
    
    setNumbers(nums);
    setGameState('playing');
    setCurrentPrimeIndex(0);
    setStartTime(Date.now());
    setElapsedTime(0);
    setMistakes(0);
    setMessage(`Eliminate all multiples of ${PRIMES_TO_SIEVE[0]}!`);
  };

  const handleNumberClick = (numObj, index) => {
    if (gameState !== 'playing') return;
    if (numObj.status !== 'active') return;
    if (numObj.value === currentPrime) return; // Don't eliminate the prime itself!

    if (numObj.value % currentPrime === 0) {
        // Correct elimination
        const newNumbers = [...numbers];
        newNumbers[index].status = 'eliminated';
        setNumbers(newNumbers);
        
        // Check if phase is complete
        checkPhaseCompletion(newNumbers);
    } else {
        // Mistake
        setMistakes(m => m + 1);
        setMessage("Oops! That's not a multiple!");
        // Small penalty? Visual shake?
    }
  };

  const checkPhaseCompletion = (currentNumbers) => {
    // Check if any multiples of currentPrime are still 'active'
    const remainingMultiples = currentNumbers.filter(n => 
        n.status === 'active' && 
        n.value > currentPrime && 
        n.value % currentPrime === 0
    );

    if (remainingMultiples.length === 0) {
        // Phase Complete!
        if (currentPrimeIndex < PRIMES_TO_SIEVE.length - 1) {
            // Next Phase
            const nextPrime = PRIMES_TO_SIEVE[currentPrimeIndex + 1];
            setCurrentPrimeIndex(prev => prev + 1);
            setMessage(`Great! Now eliminate multiples of ${nextPrime}!`);
        } else {
            // All phases done! Victory!
            finishGame(currentNumbers);
        }
    }
  };

  const finishGame = (finalNumbers) => {
      setGameState('won');
      // Reveal all remaining active numbers as Primes
      const revealed = finalNumbers.map(n => ({
          ...n,
          status: n.status === 'active' ? 'prime' : n.status
      }));
      setNumbers(revealed);
      setMessage("Sieve Complete! The remaining numbers are Primes.");
  };

  const formatTime = (ms) => {
      return (ms / 1000).toFixed(1);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center p-4">
      {/* Header */}
      <div className="w-full max-w-2xl mb-4">
        <button
            onClick={() => window.location.href = "/games"}
            className="flex items-center text-slate-500 hover:text-slate-800 transition-colors"
        >
            <span className="mr-2">←</span> Back to Games
        </button>
      </div>

      <div className="w-full max-w-2xl flex justify-between items-end mb-6">
        <div>
            <h1 className="text-3xl font-bold text-slate-800">The Sieve Speedrun</h1>
            <p className="text-slate-500">Find the primes by elimination!</p>
        </div>
        <div className="flex gap-4 text-xl font-mono">
            <div className="flex items-center gap-2 text-blue-600">
                <Timer size={20} /> {formatTime(elapsedTime)}s
            </div>
            {mistakes > 0 && (
                <div className="flex items-center gap-2 text-red-500">
                    <AlertCircle size={20} /> {mistakes}
                </div>
            )}
        </div>
      </div>

      {/* Game Area */}
      <div className="bg-white p-6 rounded-2xl shadow-xl w-full max-w-2xl">
         
         {gameState === 'playing' && (
             <div className="mb-6 bg-blue-50 p-4 rounded-xl text-center border-2 border-blue-100 animate-pulse">
                 <span className="text-2xl font-bold text-blue-800">{message}</span>
             </div>
         )}
         
         {gameState === 'won' && (
             <div className="mb-6 bg-green-100 p-6 rounded-xl text-center border-2 border-green-300">
                 <h2 className="text-3xl font-bold text-green-800 mb-2 flex items-center justify-center gap-2">
                     <Trophy className="text-yellow-500" /> Sieve Complete!
                 </h2>
                 <p className="text-green-700 text-lg">
                     You found all primes up to 100 in <span className="font-bold">{formatTime(elapsedTime)}s</span>!
                 </p>
                 <button onClick={startGame} className="mt-4 px-6 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-500 transition flex items-center gap-2 mx-auto">
                     <RefreshCw size={20} /> Play Again
                 </button>
             </div>
         )}

         {gameState === 'menu' && (
             <div className="absolute inset-0 bg-white/90 z-10 flex flex-col items-center justify-center rounded-2xl">
                 <h2 className="text-4xl font-bold text-slate-800 mb-6">Ready to Sieve?</h2>
                 <div className="max-w-md text-center text-slate-600 mb-8 space-y-2">
                     <p>1. We will call out a Prime (2, 3, 5, 7).</p>
                     <p>2. Click <strong>ALL</strong> its multiples to eliminate them.</p>
                     <p>3. Do it fast! The survivors are the Primes.</p>
                 </div>
                 <button onClick={startGame} className="px-10 py-4 bg-blue-600 text-white rounded-xl font-bold text-2xl hover:bg-blue-500 transition shadow-lg hover:scale-105 active:scale-95 flex items-center gap-3">
                     <Play /> Start Speedrun
                 </button>
             </div>
         )}
         
         {/* The Grid */}
         <div className="grid grid-cols-10 gap-2 sm:gap-3">
            {numbers.map((num, i) => {
                let statusClass = "bg-slate-100 text-slate-700 hover:bg-slate-200";
                
                if (num.status === 'eliminated') {
                    statusClass = "bg-slate-50 text-slate-300 opacity-50 scale-90";
                } else if (num.status === 'prime') {
                    statusClass = "bg-yellow-400 text-yellow-900 font-bold shadow-lg ring-2 ring-yellow-200 scale-110";
                } else if (gameState === 'playing' && num.value === currentPrime) {
                    statusClass = "bg-blue-500 text-white font-bold ring-4 ring-blue-200 z-10 scale-110";
                }

                // Interaction Disable conditions
                const isClickable = gameState === 'playing' && num.status === 'active' && num.value !== currentPrime;

                return (
                    <button 
                        key={num.value}
                        onClick={() => handleNumberClick(num, i)}
                        disabled={!isClickable}
                        className={`
                            aspect-square rounded-lg flex items-center justify-center text-sm sm:text-lg font-bold transition-all duration-200
                            ${statusClass}
                            ${isClickable ? 'cursor-pointer' : 'cursor-default'}
                        `}
                    >
                        {num.value}
                    </button>
                )
            })}
         </div>

      </div>
    </div>
  );
};

export default SieveSpeedrun;
